import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { PowerState, PowerPlant, PowerPlantType } from "./types";

const initialState: PowerState = {
  shipSize: 100,
  techLevel: 12,
  powerPlant: null,
  drives: [],
  bridge: null,
  sensors: null,
  weapons: [],
  defensiveSystems: [],
  optionalSystems: [],
  stateroomConfig: null,
  breakawayHull: {
    isBreakaway: false,
    sections: [],
    combinedPerformance: {
      maneuver: -1,
      jump: -1,
      reaction: -1,
      totalPower: 0,
      totalCost: 0,
      totalWeight: 0,
    },
  },
  isLoading: false,
  error: null,
};

const powerSlice = createSlice({
  name: "power",
  initialState,
  reducers: {
    // Ship size and tech level
    setShipSize: (state, action: PayloadAction<number>) => {
      state.shipSize = action.payload;
      state.error = null;
    },
    
    setTechLevel: (state, action: PayloadAction<number>) => {
      state.techLevel = action.payload;
      state.error = null;
    },

    // Power plant actions
    setPowerPlant: (state, action: PayloadAction<PowerPlant | null>) => {
      state.powerPlant = action.payload;
      state.error = null;
    },

    setPowerPlantType: (state, action: PayloadAction<PowerPlantType>) => {
      if (state.powerPlant) {
        state.powerPlant.type = action.payload;
      } else {
        // Create new power plant with default values
        state.powerPlant = {
          type: action.payload,
          rating: 1, // Default rating
          cost: 0,
          weight: 0,
          power: 0,
          techLevel: state.techLevel,
          // fuelEfficiency: 1,
          maintenanceCost: 0,
          description: "",
        };
      }
      state.error = null;
    },



    setPowerPlantWeight: (state, action: PayloadAction<number>) => {
      if (state.powerPlant) {
        state.powerPlant.weight = action.payload;
      }
      state.error = null;
    },

    clearPowerPlant: (state) => {
      state.powerPlant = null;
      state.error = null;
    },

    // System dependencies
    setDrives: (state, action: PayloadAction<any[]>) => {
      state.drives = action.payload;
      state.error = null;
    },

    setBridge: (state, action: PayloadAction<any>) => {
      state.bridge = action.payload;
      state.error = null;
    },

    setSensors: (state, action: PayloadAction<any>) => {
      state.sensors = action.payload;
      state.error = null;
    },

    setWeapons: (state, action: PayloadAction<any[]>) => {
      state.weapons = action.payload;
      state.error = null;
    },

    setDefensiveSystems: (state, action: PayloadAction<any[]>) => {
      state.defensiveSystems = action.payload;
      state.error = null;
    },

    setOptionalSystems: (state, action: PayloadAction<any[]>) => {
      state.optionalSystems = action.payload;
      state.error = null;
    },

    setStateroomConfig: (state, action: PayloadAction<any>) => {
      state.stateroomConfig = action.payload;
      state.error = null;
    },

    // Breakaway hull actions
    setBreakawayHull: (state, action: PayloadAction<boolean>) => {
      state.breakawayHull = {
        ...state.breakawayHull,
        isBreakaway: action.payload,
        sections: state.breakawayHull?.sections || [],
        combinedPerformance: state.breakawayHull?.combinedPerformance || {},
      };
      state.error = null;
    },

    setBreakawaySections: (state, action: PayloadAction<any[]>) => {
      state.breakawayHull = {
        ...state.breakawayHull,
        isBreakaway: state.breakawayHull?.isBreakaway || false,
        sections: action.payload,
        combinedPerformance: state.breakawayHull?.combinedPerformance || {},
      };
      state.error = null;
    },

    updateBreakawaySection: (state, action: PayloadAction<{ sectionId: string; updates: any }>) => {
      const { sectionId, updates } = action.payload;
      if (!state.breakawayHull?.sections) return;
      
      const sectionIndex = state.breakawayHull.sections.findIndex(s => s.id === sectionId);
      
      if (sectionIndex !== -1) {
        state.breakawayHull.sections[sectionIndex] = {
          ...state.breakawayHull.sections[sectionIndex],
          ...updates,
        };
      }
      state.error = null;
    },

    updateBreakawayPowerPlant: (state, action: PayloadAction<{ sectionId: string; powerPlant: PowerPlant | null }>) => {
      const { sectionId, powerPlant } = action.payload;
      if (!state.breakawayHull?.sections) return;
      
      const sectionIndex = state.breakawayHull.sections.findIndex(s => s.id === sectionId);
      
      if (sectionIndex !== -1) {
        state.breakawayHull.sections[sectionIndex].powerPlant = powerPlant;
      }
      state.error = null;
    },

    updateCombinedPowerPlant: (state, action: PayloadAction<PowerPlant | null>) => {
      state.breakawayHull = {
        ...state.breakawayHull,
        isBreakaway: state.breakawayHull?.isBreakaway || false,
        sections: state.breakawayHull?.sections || [],
        combinedPerformance: state.breakawayHull?.combinedPerformance || {},
      };
      state.error = null;
    },

    // Loading and error states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Reset to initial state
    reset: (state) => {
      return initialState;
    },
  },
});

export const {
  setShipSize,
  setTechLevel,
  setPowerPlant,
  setPowerPlantType,
  setPowerPlantWeight,
  clearPowerPlant,
  setDrives,
  setBridge,
  setSensors,
  setWeapons,
  setDefensiveSystems,
  setOptionalSystems,
  setStateroomConfig,
  setBreakawayHull,
  setBreakawaySections,
  updateBreakawaySection,
  updateBreakawayPowerPlant,
  updateCombinedPowerPlant,
  setLoading,
  setError,
  reset,
} = powerSlice.actions;

export default powerSlice.reducer;
